/**
 * The country the device is in, for the few things that depend on it rather than
 * on the language: the BUN field's unit in Spanish (labDisplayUnit.ts,
 * DECISIONS.md DS-6) and which support services are listed
 * (content/supportResources.ts).
 *
 * It is the region subtag of the browser's locale ("es-MX" → "MX"), taken from the
 * device in hand every time, as DS-5 takes the timezone — never stored. Spanish
 * spans some twenty countries, and an owner who moves or travels should get the
 * services of where they are.
 */

/** The region subtag of a locale, upper-cased: "es-MX" → "MX", "es_ar" → "AR",
 *  "es-419" → "419"; null when it has none ("es"). */
export function regionOfLocale(locale: string | null | undefined): string | null {
  const m = /^[a-z]{2,3}(?:[-_][a-z]{4})?[-_]([a-z]{2}|\d{3})(?:[-_]|$)/i.exec(locale ?? '');
  return m ? m[1].toUpperCase() : null;
}

/**
 * The device's country for a reader of `lang`: the region of the first browser
 * locale in that language ("es-AR" in a list headed by "en-US" when the owner reads
 * Spanish), else the region of the browser's own locale; null when neither says.
 */
export function deviceCountry(
  lang: string | null | undefined,
  locales: readonly string[] = browserLocales(),
): string | null {
  const base = (lang ?? '').split('-')[0].toLowerCase();
  const inLanguage = locales.find((l) => l.toLowerCase().split(/[-_]/)[0] === base && regionOfLocale(l));
  return regionOfLocale(inLanguage) ?? regionOfLocale(locales[0]);
}

function browserLocales(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}
